import PropTypes from "prop-types";
import React, { Component } from "react";
import {
  Grid,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from "@material-ui/core";
import {
  Visibility as VisibilityIcon,
  Replay as ReplayIcon,
  GetApp as GetAppIcon,
  ErrorOutline as ErrorOutlineIcon
} from "@material-ui/icons";
import { Link, withRouter } from "react-router-dom";
import { withAlert } from "react-alert";
import moment from "moment";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import styled from "styled-components";
import { withTranslation } from "react-i18next";
import Heading from "../../waybee-ui/Heading/Heading";
import Group from "../../waybee-ui/Group";
import TableGrid from "../../waybee-ui/TableGrid";
import JobsService from "../../services/JobsService";
import { setJobsQueue } from "../../redux/actions";
import {
  getJobDisplayStatus,
  getJobStatusIcon,
  getJobStatusColor,
  getSmartMinutesJobLink,
  canResendJob,
  isJobInProgress
} from "../../utils/jobHelpers";
import JobPayloadDialog from "./components/JobPayloadDialog";

const PAGE_SIZE = 20;

const JOB_TYPE_KEYS = {
  save_grades: "grades",
  save_frequency: "frequency",
  save_occurrences: "occurrences",
  generate_class_report: "report",
  sync_lb: "lbSync",
  process_smart_minutes: "smartMinutes",
  save_acl_profile: "aclProfile",
  save_class_plan: "classPlan",
  save_class_stage: "classStage"
};

const PENDING_MESSAGE_KEYS = {
  save_frequency: "pendingFrequency",
  save_occurrences: "pendingOccurrences",
  generate_class_report: "pendingReport",
  sync_lb: "pendingLbSync",
  process_smart_minutes: "pendingSmartMinutes",
  save_acl_profile: "pendingAclProfile",
  save_class_plan: "pendingClassPlan",
  save_class_stage: "pendingClassStage"
};

const SUCCESS_MESSAGE_KEYS = {
  save_frequency: "successFrequency",
  save_occurrences: "successOccurrences",
  generate_class_report: "successReport",
  sync_lb: "successLbSync",
  process_smart_minutes: "successSmartMinutes",
  save_acl_profile: "successAclProfile",
  save_class_plan: "successClassPlan",
  save_class_stage: "successClassStage"
};

const ERROR_MESSAGE_KEYS = {
  save_frequency: "errorFrequency",
  save_occurrences: "errorOccurrences",
  generate_class_report: "errorReport",
  sync_lb: "errorLbSync",
  process_smart_minutes: "errorSmartMinutes",
  save_acl_profile: "errorAclProfile",
  save_class_plan: "errorClassPlan",
  save_class_stage: "errorClassStage"
};

const getLocalizedJobTypeLabel = (name, t) => {
  const key = JOB_TYPE_KEYS[name];
  return key ? t(`jobs.type.${key}`) : name;
};

const getLocalizedJobStatusLabel = (displayStatus, t) => {
  const statusMap = {
    pending: "pending",
    paused: "paused",
    success: "success",
    warning: "warning",
    error: "error"
  };
  return t(`jobs.status.${statusMap[displayStatus] || displayStatus}`);
};

const getLocalizedJobStatusMessage = (job, t) => {
  if (!job) return "";
  if (isJobInProgress(job)) {
    if (job.status === "paused") return t("jobs.messages.paused");
    if (job.status === "retrying") return t("jobs.messages.retrying");
    const key = PENDING_MESSAGE_KEYS[job.name] || "pendingGrades";
    return t(`jobs.messages.${key}`);
  }
  if (job.status === "success") {
    if (job.error) return t("jobs.messages.partialError");
    const key = SUCCESS_MESSAGE_KEYS[job.name] || "successGrades";
    return t(`jobs.messages.${key}`);
  }
  if (job.status === "error") {
    const key = ERROR_MESSAGE_KEYS[job.name] || "errorGrades";
    return t(`jobs.messages.${key}`);
  }
  return "";
};

const getLocalizedJobAttemptsMessage = (job, t) => {
  const attempts = Number(job && job.attempts) || 0;
  if (attempts <= 0) return "";
  if (attempts === 1) return t("jobs.messages.attemptsOne");
  return t("jobs.messages.attemptsOther", { count: attempts });
};

const MetaLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

const MetaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
`;

const JobsTable = styled.div`
  && h5 {
    font-size: 16px;
  }
`;

const AttemptsText = styled.span`
  font-size: 12px;
  color: #888;
`;

const StatusChip = styled(Chip)`
  && {
    background-color: ${({ $statusColor }) => $statusColor};
    color: ${({ $displayStatus }) =>
      $displayStatus === "warning" || $displayStatus === "paused"
        ? "#333"
        : "#fff"};
    font-weight: 500;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const ActionButton = styled(Button)`
  && {
    text-transform: none;
    font-weight: 500;
    border-radius: 6px;
    padding: 4px 10px;
    min-height: 32px;
    line-height: 1.2;

    .MuiButton-startIcon {
      margin-right: 6px;
      margin-left: -2px;
    }

    .MuiSvgIcon-root {
      font-size: 16px;
    }
  }
`;

const ViewErrorButton = styled(ActionButton)`
  && {
    color: #ee4848;
    border-color: #ee4848;
    font-weight: 600;
    min-height: 28px;
    padding: 2px 8px;

    &:hover {
      background-color: rgba(238, 72, 72, 0.08);
      border-color: #d63c3c;
    }
  }
`;

class JobRequests extends Component {
  constructor(props) {
    super(props);
    this.state = {
      jobs: [],
      total: 0,
      skip: 0,
      loading: false,
      filters: {
        job: "",
        status: ""
      },
      payloadDialog: {
        open: false,
        payload: null,
        response: null,
        title: ""
      }
    };
  }

  componentDidMount() {
    this.loadJobs(true);

    window.onscroll = () => {
      const container = document.querySelector("html");
      if (container.scrollTop + window.innerHeight >= container.offsetHeight) {
        this.loadMoreJobs();
      }
    };
  }

  componentWillUnmount() {
    window.onscroll = null;
  }

  loadJobs = async (reset = false) => {
    const { loading, skip, jobs, filters } = this.state;
    if (loading) return;

    this.setState({ loading: true });

    try {
      const currentSkip = reset ? 0 : skip;
      const { items, total } = await JobsService.getJobHistory({
        limit: PAGE_SIZE,
        skip: currentSkip,
        job: filters.job || undefined,
        status: filters.status || undefined
      });

      this.setState(
        {
          jobs: reset ? items : jobs.concat(items),
          total,
          skip: currentSkip + items.length,
          loading: false
        },
        () => this.markItemsAsRead(items)
      );
    } catch (error) {
      this.setState({ loading: false });
    }
  };

  loadMoreJobs = () => {
    const { jobs, total, loading } = this.state;
    if (loading || jobs.length >= total) return;
    this.loadJobs(false);
  };

  handleFilterChange = (field, value) => {
    this.setState(
      prev => ({
        filters: { ...prev.filters, [field]: value },
        skip: 0
      }),
      () => this.loadJobs(true)
    );
  };

  markItemsAsRead = async items => {
    const unread = (items || []).filter(
      job => job && !job.readAt && !isJobInProgress(job)
    );
    if (!unread.length) return;

    await Promise.all(unread.map(job => JobsService.markAsRead(job.id)));

    const queue = await JobsService.getJobQueue();
    const { setJobsQueue: onSetJobsQueue } = this.props;
    onSetJobsQueue(queue);

    const unreadIds = new Set(unread.map(job => job.id));
    const readAt = new Date().toISOString();
    this.setState(prev => ({
      jobs: prev.jobs.map(item =>
        unreadIds.has(item.id) ? { ...item, readAt } : item
      )
    }));
  };

  showError = errorMessage => {
    const { alert, t } = this.props;
    alert.show(errorMessage, { title: t("common.messages.errorOccurred") });
  };

  downloadReport = async (e, job) => {
    e.preventDefault();
    e.stopPropagation();
    const { alert, t } = this.props;
    try {
      const file = await JobsService.downloadJobFile(job.id);
      const fileName =
        (file && file.fileName) ||
        (job.download && job.download.fileName) ||
        "relatorio.pdf";
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${file.base64}`;
      link.download = fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      alert.show(t("jobs.downloadError"), { title: t("common.actions.error") });
    }
  };

  openPayloadDialog = (e, job) => {
    e.preventDefault();
    e.stopPropagation();
    const { t } = this.props;
    this.setState({
      payloadDialog: {
        open: true,
        payload: job.payload,
        response: job.response,
        title: job.title || t("jobs.payload.title")
      }
    });
  };

  closePayloadDialog = () => {
    this.setState({
      payloadDialog: {
        open: false,
        payload: null,
        response: null,
        title: ""
      }
    });
  };

  handleResend = async (e, job) => {
    e.preventDefault();
    e.stopPropagation();
    const { alert, t } = this.props;
    try {
      await JobsService.resendJob(job.id);
      alert.show(t("jobs.resent"), {
        title: t("jobs.resendTitle"),
        type: "success"
      });
      this.loadJobs(true);
      const queue = await JobsService.getJobQueue();
      const { setJobsQueue: onSetJobsQueue } = this.props;
      onSetJobsQueue(queue);
    } catch (error) {
      const message =
        (error &&
          error.response &&
          error.response.data &&
          error.response.data.error &&
          error.response.data.error.message) ||
        t("jobs.resendError");
      alert.show(message, { title: t("common.messages.errorOccurred") });
    }
  };

  render() {
    const { jobs, total, filters, payloadDialog } = this.state;
    const { t } = this.props;

    return (
      <div>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Group>
              <Box>
                <Box
                  display="flex"
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                  style={{ gap: 16 }}
                >
                  <Heading level={1} icon="history" gutterBottom>
                    {t("jobs.title", { total })}
                  </Heading>
                  <Box display="flex" style={{ gap: 16 }}>
                    <FormControl
                      variant="outlined"
                      size="small"
                      style={{ minWidth: 140 }}
                    >
                      <InputLabel>{t("jobs.filter.type")}</InputLabel>
                      <Select
                        value={filters.job}
                        onChange={e =>
                          this.handleFilterChange("job", e.target.value)}
                        label={t("jobs.filter.type")}
                      >
                        <MenuItem value="">{t("common.actions.all")}</MenuItem>
                        <MenuItem value="save_grades">
                          {t("jobs.type.grades")}
                        </MenuItem>
                        <MenuItem value="save_frequency">
                          {t("jobs.type.frequency")}
                        </MenuItem>
                        <MenuItem value="save_occurrences">
                          {t("jobs.type.occurrences")}
                        </MenuItem>
                        <MenuItem value="generate_class_report">
                          {t("jobs.type.report")}
                        </MenuItem>
                        <MenuItem value="process_smart_minutes">
                          {t("jobs.type.smartMinutes")}
                        </MenuItem>
                        <MenuItem value="sync_lb">
                          {t("jobs.type.lbSync")}
                        </MenuItem>
                        <MenuItem value="save_acl_profile">
                          {t("jobs.type.aclProfile")}
                        </MenuItem>
                        <MenuItem value="save_class_plan">
                          {t("jobs.type.classPlan")}
                        </MenuItem>
                        <MenuItem value="save_class_stage">
                          {t("jobs.type.classStage")}
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl
                      variant="outlined"
                      size="small"
                      style={{ minWidth: 160 }}
                    >
                      <InputLabel>{t("jobs.filter.status")}</InputLabel>
                      <Select
                        value={filters.status}
                        onChange={e =>
                          this.handleFilterChange("status", e.target.value)}
                        label={t("jobs.filter.status")}
                      >
                        <MenuItem value="">{t("common.actions.all")}</MenuItem>
                        <MenuItem value="pending">
                          {t("jobs.status.pending")}
                        </MenuItem>
                        <MenuItem value="retrying">
                          {t("jobs.status.retrying")}
                        </MenuItem>
                        <MenuItem value="paused">
                          {t("jobs.status.paused")}
                        </MenuItem>
                        <MenuItem value="success">
                          {t("jobs.status.success")}
                        </MenuItem>
                        <MenuItem value="error">
                          {t("jobs.status.error")}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
                <JobsTable>
                  <TableGrid>
                    {jobs.map(job => {
                      const displayStatus = getJobDisplayStatus(job);
                      const statusColor = getJobStatusColor(displayStatus);
                      const attemptsMessage = getLocalizedJobAttemptsMessage(
                        job,
                        t
                      );
                      const ataLink =
                        displayStatus === "success"
                          ? getSmartMinutesJobLink(job)
                          : null;

                      return (
                        <TableGrid.Row
                          photo="#"
                          size="small"
                          key={job.id}
                          icon={getJobStatusIcon(displayStatus)}
                          highlight={!job.readAt}
                        >
                          <TableGrid.Title xs={12} md={7}>
                            {job.title || getLocalizedJobTypeLabel(job.name, t)}
                            <MetaBlock>
                              <MetaLine>
                                <StatusChip
                                  size="small"
                                  label={getLocalizedJobStatusLabel(
                                    displayStatus,
                                    t
                                  )}
                                  $statusColor={statusColor}
                                  $displayStatus={displayStatus}
                                />
                                <span>
                                  {getLocalizedJobStatusMessage(job, t)}
                                </span>
                                {job.error && (
                                  <ViewErrorButton
                                    size="small"
                                    variant="outlined"
                                    startIcon={<ErrorOutlineIcon />}
                                    onClick={e => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      this.showError(job.error);
                                    }}
                                  >
                                    {t("jobs.viewError")}
                                  </ViewErrorButton>
                                )}
                              </MetaLine>
                              {attemptsMessage && (
                                <AttemptsText>{attemptsMessage}</AttemptsText>
                              )}
                            </MetaBlock>
                          </TableGrid.Title>
                          <TableGrid.Text xs={12} md={3}>
                            <Actions>
                              <ActionButton
                                size="small"
                                color="primary"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={e => this.openPayloadDialog(e, job)}
                              >
                                {t("jobs.viewData")}
                              </ActionButton>
                              {job.download && job.download.available && (
                                <ActionButton
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  startIcon={<GetAppIcon />}
                                  onClick={e => this.downloadReport(e, job)}
                                >
                                  {t("jobs.downloadPdf")}
                                </ActionButton>
                              )}
                              {ataLink && (
                                <ActionButton
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  component={Link}
                                  to={ataLink}
                                  startIcon={<VisibilityIcon />}
                                >
                                  {t("jobs.openAta")}
                                </ActionButton>
                              )}
                              {canResendJob(job) && (
                                <ActionButton
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  startIcon={<ReplayIcon />}
                                  onClick={e => this.handleResend(e, job)}
                                >
                                  {t("jobs.resend")}
                                </ActionButton>
                              )}
                            </Actions>
                          </TableGrid.Text>
                          <TableGrid.Text xs={12} md={2} align="right">
                            {job.createdAt
                              ? moment(job.createdAt).format(
                                  "DD/MM/YYYY [às] HH:mm"
                                )
                              : "-"}
                          </TableGrid.Text>
                        </TableGrid.Row>
                      );
                    })}
                  </TableGrid>
                </JobsTable>
              </Box>
            </Group>
          </Grid>
        </Grid>
        <JobPayloadDialog
          open={payloadDialog.open}
          onClose={this.closePayloadDialog}
          payload={payloadDialog.payload}
          response={payloadDialog.response}
          title={payloadDialog.title}
        />
      </div>
    );
  }
}

JobRequests.propTypes = {
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  setJobsQueue: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
};

const mapDispatchToProps = dispatch =>
  bindActionCreators({ setJobsQueue }, dispatch);

export default connect(
  null,
  mapDispatchToProps
)(withAlert()(withRouter(withTranslation()(JobRequests))));
